/* eslint-disable max-statements */
import { useCallback, useMemo } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import {
  ProductCloneResponseDto,
  ProductDetailsResponseDto,
} from '@tiendanube/common';
import useCanUseUnstableApiV2 from 'App/hooks/useCanUseUnstableApiV2';
import { LanguagesType } from 'App/i18n';
import { AppDispatchType, RootStateType, useAppDispatch } from 'App/store';
import { StatusObjectType } from 'commons/types';
import { useGetLanguage } from 'domains/Auth/hooks';
import { cleanUpInventory } from 'domains/Catalog/Inventory/inventorySlice';
import { productPayloadBuilder } from 'domains/Catalog/Products/ProductPayloadBuilder/productPayloadBuilder';
import {
  CloneProductToParamsInterface,
  UpdateProductParamsInterface,
} from 'domains/Catalog/Products/productsServices';
import {
  cleanMetafieldsPersistsStatus as cleanMetafieldsPersistsStatusAction,
  cleanCreateHighlightProductStatus as cleanCreateHighlightProductStatusAction,
  cleanInvoiceSettingsError as cleanInvoiceSettingsErrorAction,
  MetafieldsPersistsStatusType,
  updateProduct as updateProductAction,
  updateProductWithUnstableApi as updateProductWithUnstableApiAction,
} from 'domains/Catalog/Products/productsSlice';
import {
  createMetafieldsPersistsStatusById,
  createHighlightProductStatus as createHighlightProductStatusSelector,
  createGetProductByIdStatus,
} from 'domains/Catalog/Products/productsSlice/productSelectors';
import { useProductActions } from '../../../hooks';
import { ProductFormState } from '../../../hooks/useProductForm/types';
import useDigestedProduct from '../useDigestedProduct';

export interface UseProductEditResult {
  product: ProductFormState;
  status: StatusObjectType;
  metafieldsPersistsStatus: MetafieldsPersistsStatusType;
  updateProduct: (
    params: UpdateProductParamsInterface,
  ) => Promise<ProductDetailsResponseDto>;
  deleteProduct: (id: string) => Promise<void>;
  cloneProduct: (
    productToClone: CloneProductToParamsInterface,
  ) => Promise<ProductCloneResponseDto>;
  cleanMetafieldsPersistsStatus: () => void;
  cleanCreateHighlightProductStatus: () => void;
  cleanInvoiceSettingsError: () => void;
  updateHighlightProductError: boolean;
}

function useEditProduct(id: string): UseProductEditResult {
  const dispatch = useAppDispatch();
  const language = useGetLanguage();
  const canUseUnstableApiV2 = useCanUseUnstableApiV2();
  const product = useDigestedProduct(id);

  const status = useSelector((state: RootStateType) =>
    createGetProductByIdStatus(state),
  );

  const getMetafieldsPersistsStatusById = useMemo(
    createMetafieldsPersistsStatusById,
    [],
  );

  const createHighlightProductStatus = useSelector(
    createHighlightProductStatusSelector,
  );

  const metafieldsPersistsStatusById = useSelector((state: RootStateType) =>
    getMetafieldsPersistsStatusById(state, id),
  );

  const { cloneProduct, deleteProduct } = useProductActions();

  const updateProduct = useCallback(
    async (data: UpdateProductParamsInterface) => {
      if (canUseUnstableApiV2) {
        return applyApiV2(data, product, dispatch, language);
      } else {
        return applyApiV1(data, dispatch);
      }
    },
    [dispatch, canUseUnstableApiV2, language, product],
  );

  const cleanMetafieldsPersistsStatus = useCallback(async () => {
    dispatch(cleanMetafieldsPersistsStatusAction({ id }));
  }, [dispatch, id]);

  const cleanCreateHighlightProductStatus = useCallback(async () => {
    dispatch(cleanCreateHighlightProductStatusAction());
  }, [dispatch]);

  const cleanInvoiceSettingsError = useCallback(async () => {
    dispatch(cleanInvoiceSettingsErrorAction({ id }));
  }, [dispatch, id]);

  const returnMemo = useMemo(
    () => ({
      product,
      status,
      metafieldsPersistsStatus: metafieldsPersistsStatusById,
      updateHighlightProductError:
        product &&
        !!(
          product.sectionCodes.includes('update-error') ||
          createHighlightProductStatus?.includes('update-error')
        ),
      deleteProduct,
      cloneProduct,
      updateProduct,
      cleanMetafieldsPersistsStatus,
      cleanCreateHighlightProductStatus,
      cleanInvoiceSettingsError,
    }),
    [
      product,
      status,
      metafieldsPersistsStatusById,
      createHighlightProductStatus,
      deleteProduct,
      cloneProduct,
      updateProduct,
      cleanMetafieldsPersistsStatus,
      cleanCreateHighlightProductStatus,
      cleanInvoiceSettingsError,
    ],
  );

  return returnMemo;
}

const applyApiV1 = async (
  data: UpdateProductParamsInterface,
  dispatch: AppDispatchType,
) => {
  const result = await dispatch(updateProductAction(data));
  dispatch(cleanUpInventory());
  return unwrapResult(result);
};

const applyApiV2 = async (
  data: UpdateProductParamsInterface,
  currentProduct: ProductFormState,
  dispatch: AppDispatchType,
  language: LanguagesType,
) => {
  const { id, product } = data;
  const payload = productPayloadBuilder.update(
    currentProduct,
    product,
    language,
  );
  const result = await dispatch(
    updateProductWithUnstableApiAction({ id, product: payload }),
  );
  dispatch(cleanUpInventory());
  return unwrapResult(result);
};

export default useEditProduct;
